import Alpine from 'alpinejs'

/**
 * Manages Event Search filters + async fetching of results
 *
 * @see src/templates/_components/preBuilt/eventEntries.twig
 * @see src/modules/frontend/services/EventSearch.php
 *
 * @property $refs
 * @property $store
 */
export default ({
  // initial state is passed in from the template, which in turn gets it from the controller
  dateRange,
  dateRanges,
  defaultDateRange,
  eventType,
  eventTypes,
  location,
  locations,
  filters = [],
}) => {
  return {
    /****** Start data ******/
    desktopBreakpoint: '(min-width: 768px)', // TW md
    isDesktop: false, // is the current screen a desktop class width?
    isLoading: false, // is an update is in progress?
    mobileFiltersOpen: false, // are the mobile filters are open?
    dateRange, // the currently selected date range
    dateRanges, // the available date ranges
    defaultDateRange, // the default date range
    eventType, // the currently selected event types
    eventTypes, // the available event types
    allEventTypes: eventTypes, // all of the available event types
    location, // the currently selected states
    locations, // the available states
    allLocations: locations, // all of the available states
    filters, // the available filter config
    errorMessage: '', // the error message to display
    /****** End of data ******/
    /**
     * Initialize the component
     */
    init() {
      // watch for changes to the filter form and fetch results
      ['dateRange', 'eventType', 'location'].forEach((key) => {
        this.$watch(key, Alpine.throttle(async () => {
          await this.updateResults()
        }, 100))
      })
      this.watchBreakpoint()

      if(this.isDesktop) {
        this.$watch('location', Alpine.debounce(() => this.filterTypesByState(), 200))
        this.$watch('eventType', Alpine.debounce(() => this.filterStatesByType(), 200))
      }
    },
    /****** Start computed properties / getters ******/
    /**
     * Get the total number of filters currently applied
     * @return {number}
     */
    get currentFilterCount() {
      return [
        this.dateRange !== this.defaultDateRange,
        ...this.eventType,
        ...this.location,
      ].filter(Boolean)
        .length
    },
    /****** End of computed properties / getters ******/
    /****** Start methods ******/
    /**
     * Update the results with new data from the server
     * @return {Promise<void>}
     */
    async updateResults() {
      if (this.isLoading) {
        return
      }

      // Cancel any pending request
      if (this.currentRequest) {
        this.currentRequest.abort()
      }

      this.isLoading = true

      /**
       * AJAX filter updates with HTML replacement instead of page reload.
       *
       * Why this approach: This component is embedded within Craft's page builder content,
       * so we cannot modify the page template for AJAX requests. The normal approach of
       * returning a minimal template for AJAX calls isn't possible here.
       *
       * Instead, we fetch the full page, extract the results section, and replace the DOM.
       * This preserves filter state (especially mobile modal state) without page reloads.
       */
      try {
        // Prepare the filter request
        const params = this.serialize(this.$refs.filterForm)
        const url = new URL(window.location.pathname, window.location.origin)
        url.search = params.toString()

        // Set up the abort controller
        const controller = new AbortController()
        this.currentRequest = controller

        // Make AJAX request with abort controller
        const response = await fetch(url, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'text/html',
          },
          signal: controller.signal,
        })

        // Handle response
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        // Extract just the results section from the response
        const html = await response.text()
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        const newResults = doc.querySelector('[x-ref="results"]')

        // Throw an error if the results section is not found
        if (!newResults) {
          throw new Error('Invalid response: results section not found')
        }

        // Replace the current results with the new results
        const currentResults = this.$refs.results
        const parent = currentResults.parentNode
        parent.replaceChild(newResults, currentResults)

        // Update the ref to point to the new element
        this.$refs.results = newResults

        // Update URL without reload
        window.history.replaceState({}, '', url)
      } catch (error) {
        this.errorMessage = 'An error occurred while fetching the results. Please try again.'

        // Throw an error if the restults replacement fails
        throw new Error(error.message)
      } finally {
        this.isLoading = false
        this.currentRequest = null
      }
    },
    /**
     * Serialize a form to a URLSearchParams object
     * @param form
     * @return {module:url.URLSearchParams}
     */
    serialize(form) {
      const formData = new FormData(form)
      return new URLSearchParams(formData)
    },
    /**
     * Get the label for a listbox closed state
     * @param optionsKey
     * @param valueKey
     * @param fallback
     * @return {string}
     */
    getCurrentLabelFor(optionsKey, valueKey, fallback) {
      const options = this.arrayify(this[optionsKey])
      return `${
        this[valueKey].length
          ? options.filter(s => this[valueKey].includes(s.value)).map(s => s.label).join(', ')
          : (fallback ?? optionsKey)
      }`
    },
    /**
     * Get the label for the mobile filter headers, includes a summary of current state
     * (e.g. "Date Range (Next 30 Days)")
     * @param modelName
     * @param disabledModelName
     * @return {string}
     */
    filterCountLabel(modelName, disabledModelName = false) {
      const value = this[modelName]
      if (disabledModelName) {
        return `(Disabled)`
      }
      if (Array.isArray(value)) {
        return `(${value.length ? value.length : `All`} Selected)`
      }
      return `(${this.valueToLabel(this.modelToOptions(modelName), value)})`
    },
    /**
     * Convert a value (string) to an option object
     * @param options
     * @param value
     * @return {*}
     */
    valueToOption(options, value) {
      return this[options].find(v => v.value === value)
    },
    /**
     * Convert a value to a label
     * @param options
     * @param value
     * @return {*}
     */
    valueToLabel(options, value) {
      return this.valueToOption(options, value)?.label ?? value
    },
    /**
     * Unset a value from a model
     * @param model
     * @param value
     */
    removeValue(model, value) {
      this[model] = this[model].filter(v => v !== value)
    },
    /**
     * Reset the date range to the default
     */
    resetDateRange() {
      this.dateRange = this.defaultDateRange
    },
    /**
     * Convert a model name to an options key
     */
    modelToOptions(model) {
      return `${model}s`
    },
    /**
     * Handle a filter change - used for mobile filters
     * @param $event
     * @param model
     */
    handleFilterChange($event, model) {
      const { target } = $event
      const { value, checked } = target
      if (Array.isArray(this[model])) {
        if (checked) {
          this[model].push(value)
        } else {
          this[model] = this[model].filter(v => v !== value)
        }
      } else {
        this[model] = checked ? value : null
      }
    },
    /**
     * Check if a model has a given value
     * @param model
     * @param value
     * @return {boolean}
     */
    modelHasValue(model, value) {
      return this.arrayify(this[model]).includes(value)
    },
    /**
     * Convert a value to an array
     * @param value
     * @return {*|*[]}
     */
    arrayify(value) {
      return Array.isArray(value) ? value : [value]
    },
    /**
     * Clear all filters
     */
    resetAllFilters() {
      this.resetDateRange()
      this.eventType = []
      this.location = []
    },
    /**
     * Watch for changes to the desktop breakpoint
     */
    watchBreakpoint() {
      const mq = window.matchMedia(this.desktopBreakpoint)
      this.isDesktop = mq.matches

      mq.addEventListener('change', (e) => {
        this.isDesktop = e.matches
      })
    },
    /* Filter the event types list when the location filter is updated */
    filterTypesByState() {
      if (this.location.length > 0) {
        const filteredTypes = this.allLocations
          .filter(location => this.location.includes(location.value))
          .flatMap(location => location.types ?? [])
          .filter((type, index, self) => self.findIndex(t => t === type) === index) // remove duplicates
          .sort() // sort alphabetically

        // Filter the original eventTypes to only include the filtered types
        this.eventTypes = this.allEventTypes.filter(eventType =>
          filteredTypes.includes(eventType.value),
        )
      } else {
        this.eventTypes = this.allEventTypes
      }
    },
    /* Filter the states list when the event type filter is updated */
    filterStatesByType() {
      if (this.eventType.length > 0) {
        this.locations = this.allLocations.filter(state => state.types?.some(type => this.eventType.includes(type)))
      } else {
        this.locations = this.allLocations
      }
    },
  }
}
